"use client";

import { motion } from "motion/react";
import type { ReactNode } from "react";
import { useChartHover } from "./chart-context";
import { useChartLegendHover } from "./chart-legend-hover";

/**
 * Legend selection is a filter, not a nuance: the unselected series drop far
 * enough that the selected one reads as the only series on the surface.
 */
export const LEGEND_DIM_OPACITY = 0.2;

interface LegendSeriesState {
  /** The legend singles out another series. */
  isDimmed: boolean;
  /** The legend singles out this series. */
  isSelected: boolean;
}

/**
 * Resolves a series index against the legend's filter — its hover, or a
 * selection it made itself. Hover wins over selection so sweeping the legend
 * previews a filter. A selection made on chart geometry is deliberately absent
 * from both results: it highlights the legend row, it does not single a series
 * out on the surface.
 */
export function useLegendSeriesState(seriesIndex?: number): LegendSeriesState {
  const { filterIndex } = useChartLegendHover();
  if (filterIndex === null || seriesIndex === undefined) {
    return { isDimmed: false, isSelected: false };
  }
  return {
    isDimmed: filterIndex !== seriesIndex,
    isSelected: filterIndex === seriesIndex,
  };
}

interface SeriesHoverDimProps {
  /** Skip the chart-hover dim. Legend selection always dims. */
  enabled?: boolean;
  /** Opacity to fade to while the chart is being hovered. */
  dimOpacity?: number;
  /** Opacity to fade to while the legend singles out another series. */
  legendDimOpacity?: number;
  /** Tween duration in seconds. */
  durationSec?: number;
  /** Series index for multi-series legend hover dimming. */
  seriesIndex?: number;
  /** Stable chart visuals — area fill, stroke line, dashed tail, etc. */
  children: ReactNode;
}

function resolveOpacity({
  isLegendSelected,
  isLegendDimmed,
  legendDimOpacity,
  chartHoverDims,
  dimOpacity,
}: {
  isLegendSelected: boolean;
  isLegendDimmed: boolean;
  legendDimOpacity: number;
  chartHoverDims: boolean;
  dimOpacity: number;
}): number {
  // The selected series stays at full opacity even while the chart is hovered:
  // otherwise a legend filter plus a hover dims everything equally and the
  // filter reads as having done nothing.
  if (isLegendSelected) {
    return 1;
  }
  if (isLegendDimmed) {
    return legendDimOpacity;
  }
  if (chartHoverDims) {
    return dimOpacity;
  }
  return 1;
}

/**
 * Wraps stable series visuals with a hover-driven opacity animation.
 *
 * The wrapper subscribes to chart hover state internally so the parent (Area /
 * Line) can stay on the stable context slice. Children come in as a React prop:
 * because the parent is not re-rendering on hover, the children element
 * reference stays identical and React skips re-rendering them when this
 * wrapper re-renders. That keeps expensive subtrees (`SeriesDashTailOverlay`
 * and its `getPointAtLength` binary search) quiescent on cursor motion.
 */
export function SeriesHoverDim({
  enabled = true,
  dimOpacity = 0.5,
  legendDimOpacity = LEGEND_DIM_OPACITY,
  durationSec = 0.4,
  seriesIndex,
  children,
}: SeriesHoverDimProps) {
  const { tooltipData, selection } = useChartHover();
  const { toggleSelectedIndex } = useChartLegendHover();
  const { isDimmed, isSelected } = useLegendSeriesState(seriesIndex);
  const isChartHovering = tooltipData !== null || selection?.active === true;
  const opacity = resolveOpacity({
    isLegendSelected: isSelected,
    isLegendDimmed: isDimmed,
    legendDimOpacity,
    chartHoverDims: enabled && isChartHovering,
    dimOpacity,
  });
  return (
    <motion.g
      animate={{ opacity }}
      initial={{ opacity: 1 }}
      transition={{ duration: durationSec, ease: "easeInOut" }}
      onClick={
        seriesIndex !== undefined
          ? (e: { currentTarget: Element }) =>
              toggleSelectedIndex(seriesIndex, {
                element: e.currentTarget,
                kind: "series",
              })
          : undefined
      }
      style={seriesIndex !== undefined ? { cursor: "pointer" } : undefined}
    >
      {children}
    </motion.g>
  );
}

SeriesHoverDim.displayName = "SeriesHoverDim";

interface SeriesLegendDimProps {
  /** Series index to resolve against the legend selection. */
  seriesIndex?: number;
  /** Opacity to fade to while the legend singles out another series. */
  dimOpacity?: number;
  /** Tween duration in seconds. */
  durationSec?: number;
  children: ReactNode;
}

/**
 * Legend-only dim for the layers that must stay out of the chart-hover dim —
 * point markers and the hover highlight band. Without it those keep full
 * opacity over a series the legend has already filtered away.
 */
export function SeriesLegendDim({
  seriesIndex,
  dimOpacity = LEGEND_DIM_OPACITY,
  durationSec = 0.4,
  children,
}: SeriesLegendDimProps) {
  const { isDimmed } = useLegendSeriesState(seriesIndex);
  return (
    <motion.g
      animate={{ opacity: isDimmed ? dimOpacity : 1 }}
      initial={{ opacity: 1 }}
      transition={{ duration: durationSec, ease: "easeInOut" }}
    >
      {children}
    </motion.g>
  );
}

SeriesLegendDim.displayName = "SeriesLegendDim";

export default SeriesHoverDim;
